import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./ProofPaywall.module.scss"

/**
 * ProofPaywall (`/proof`) — a self-contained NON-ONBOARDING premium upgrade for
 * Private Photo Vault, built as a photographer's **CONTACT / PROOF SHEET** on a
 * light table. Shown to a free user who already keeps memories in the app.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/proof.md
 *
 * The screen is a printed proof sheet of "this month's roll": white photo paper,
 * thin black film rebates + frame numbers + sprocket perforations, the real
 * memories printed small in full colour. You pick up a red **grease pencil** and
 * LOOP the keepers — a genuine freehand lasso (point-in-polygon over each frame's
 * centre). Looped frames get tagged and "filed" to the vault; a live "KEPT n/18"
 * climbs. Tap a single frame to ring it, or "Circle the whole roll" to keep them
 * all. Nothing is ever discarded — Premium keeps every original either way; the
 * loop is a curator's ritual, not a cull.
 *
 * Chosen so EVERY ledger axis differs from prior work:
 *   - Layout:      a film contact/PROOF SHEET on a light table (numbered frames,
 *                  sprocket strips, film rebates) — no prior used one. Not By
 *                  Heart's heart-mosaic, not Tidy's album grid.
 *   - Interaction: DRAW A GREASE-PENCIL LASSO — first freehand marquee / loop
 *                  selection (point-in-polygon). Distinct from every scrub / hold /
 *                  flip / toss / dial / riffle / node-graph / draw-a-link prior.
 *   - Register:    editorial / a photographer's decisive eye — "keep the ones
 *                  worth keeping." Not fear, luxury, cozy, playful-loud, seasonal.
 *   - Art:         a LIGHT contact PRINT — white paper, black film rebates, red
 *                  chinagraph (amber) = the mark you make, brand blue = the KEPT /
 *                  filed-to-vault state. Photos stay full colour (no grey→colour
 *                  bloom; breaks both the dark-skeuomorphic and the reveal clusters).
 *   - Value:       CURATION + KEEPING — "every frame worth keeping, kept in full
 *                  quality, private, auto-organised & backed up." Reached via agency /
 *                  endowment (you chose them) + goal-gradient ("circle the whole
 *                  roll"). A curator's angle none of the priors took.
 *
 * Honest & App Store compliant: the loop selects, it never deletes — copy says so
 * ("nothing is ever deleted; Premium keeps every original either way"). Purchase is
 * never gated on the demo (tap / "whole roll" / keyboard all work; it replays).
 * Yearly is the featured, default plan and carries the most prominent price; monthly
 * is small and secondary. Save % is exact yearly-vs-monthly arithmetic, no fake
 * countdown or scarcity. Photos are bundled demo memories from `public/byheart/`
 * (no network egress).
 */

// this month's roll — 18 bundled demo memories, printed small on the proof sheet
const FRAMES = Array.from({ length: 18 }, (_, i) => {
  const n = String(i + 1).padStart(2, "0")
  return { src: `/byheart/m${n}.jpg`, label: `${i + 1}` }
})
const COLS = 3
const TOTAL = FRAMES.length

type PlanKey = "yearly" | "monthly"
type Pt = { x: number; y: number }

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

// ── tiny icons ────────────────────────────────────────────────────────────────
const Tick = ({ size = 12 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Lock = ({ size = 14 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <rect
      x={4.5}
      y={10.5}
      width={15}
      height={10}
      rx={2.6}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
    />
    <path
      d="M7.5 10.5V8a4.5 4.5 0 0 1 9 0v2.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
    />
    <circle cx={12} cy={15.2} r={1.5} fill="currentColor" />
  </svg>
)

const Pencil = ({ size = 15 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M14.5 5.5l4 4L9 19l-4.6 1L5.5 15.5 14.5 5.5z"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinejoin="round"
    />
    <path
      d="M13 7l4 4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
    />
  </svg>
)

// ── loop-path helpers ─────────────────────────────────────────────────────────
// build an svg "d" from a list of points (freehand grease line, closed)
function pointsToPath(pts: Pt[], close = true): string {
  if (pts.length < 2) return ""
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`
  for (let i = 1; i < pts.length; i++)
    d += ` L ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)}`
  if (close) d += " Z"
  return d
}

// a hand-drawn-looking closed loop around a rect (used for tap / demo / whole-roll)
function loopAroundRect(
  x: number,
  y: number,
  w: number,
  h: number,
  seed = 1,
): Pt[] {
  const cx = x + w / 2
  const cy = y + h / 2
  const pad = Math.min(Math.min(w, h) * 0.18, 16) + 4
  const rx = w / 2 + pad
  const ry = h / 2 + pad
  const pts: Pt[] = []
  const N = 26
  // start a little past 0 and overshoot past 2π so it reads like a real scribble
  const start = -1.9 + seed * 0.6
  const end = start + Math.PI * 2 + 0.7
  for (let i = 0; i <= N; i++) {
    const t = start + ((end - start) * i) / N
    const wob =
      1 + Math.sin(t * 3 + seed) * 0.045 + Math.cos(t * 5 + seed * 2) * 0.03
    pts.push({ x: cx + Math.cos(t) * rx * wob, y: cy + Math.sin(t) * ry * wob })
  }
  return pts
}

// ray-casting point-in-polygon
function inPolygon(pt: Pt, poly: Pt[]): boolean {
  let inside = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x
    const yi = poly[i].y
    const xj = poly[j].x
    const yj = poly[j].y
    const intersect =
      yi > pt.y !== yj > pt.y &&
      pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

const ProofPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  // ── pricing (real debug data, yearly featured) ──────────────────────────────
  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── proof-sheet state ────────────────────────────────────────────────────────
  const [kept, setKept] = useState<Set<number>>(new Set())
  const [lassoLoops, setLassoLoops] = useState<{ id: number; d: string }[]>([])
  const [autoLoops, setAutoLoops] = useState<Record<number, string>>({}) // per-frame loop (tap / whole-roll)
  const [draft, setDraft] = useState<Pt[]>([]) // live grease line while drawing
  const [size, setSize] = useState({ w: 0, h: 0 })
  const [demoD, setDemoD] = useState<string>("") // ghost demo loop path
  const [handedOver, setHandedOver] = useState(false)

  const sheetRef = useRef<HTMLDivElement>(null)
  const drawingRef = useRef(false)
  const draftRef = useRef<Pt[]>([])
  const movedRef = useRef(0)
  const startPtRef = useRef<Pt | null>(null)
  const loopIdRef = useRef(0)
  const rafRef = useRef<number | null>(null)

  const keptCount = kept.size
  const complete = keptCount >= TOTAL

  // measure the sheet so the svg overlay shares its pixel space
  useLayoutEffect(() => {
    const el = sheetRef.current
    if (!el) return
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight })
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // frame rects in sheet-local px (queried live so it survives layout changes)
  const frameRects = useCallback(() => {
    const el = sheetRef.current
    if (!el)
      return [] as {
        idx: number
        cx: number
        cy: number
        x: number
        y: number
        w: number
        h: number
      }[]
    const base = el.getBoundingClientRect()
    return Array.from(el.querySelectorAll<HTMLElement>("[data-frame]")).map(
      (f) => {
        const r = f.getBoundingClientRect()
        const idx = Number(f.dataset.frame)
        return {
          idx,
          x: r.left - base.left,
          y: r.top - base.top,
          w: r.width,
          h: r.height,
          cx: r.left - base.left + r.width / 2,
          cy: r.top - base.top + r.height / 2,
        }
      },
    )
  }, [])

  const fileToVault = useCallback((idxs: number[]) => {
    if (!idxs.length) return
    setKept((prev) => {
      const next = new Set(prev)
      idxs.forEach((i) => next.add(i))
      return next
    })
    native.logMixpanelEvent("html_proof_kept", { count: idxs.length })
  }, [])

  // ── the teaching ghost: draw a loop around a small cluster, then commit ──────
  useEffect(() => {
    if (size.w === 0) return
    if (reduceMotion) {
      setHandedOver(true)
      return
    }
    let cancelled = false
    const t = window.setTimeout(() => {
      if (cancelled) return
      const rects = frameRects()
      const targets = rects.filter((r) => [4, 5, 8].includes(r.idx)) // a middle cluster
      if (targets.length === 0) {
        setHandedOver(true)
        return
      }
      const minX = Math.min(...targets.map((r) => r.x))
      const minY = Math.min(...targets.map((r) => r.y))
      const maxX = Math.max(...targets.map((r) => r.x + r.w))
      const maxY = Math.max(...targets.map((r) => r.y + r.h))
      const loop = loopAroundRect(minX, minY, maxX - minX, maxY - minY, 1.4)
      const d = pointsToPath(loop)
      setDemoD(d)
      // after the CSS draw animation, commit the cluster and hand over
      const commit = window.setTimeout(() => {
        if (cancelled) return
        setLassoLoops((l) => [...l, { id: ++loopIdRef.current, d }])
        setDemoD("")
        fileToVault(targets.map((t2) => t2.idx))
        setHandedOver(true)
      }, 1350)
      rafRef.current = commit as unknown as number
    }, 620)
    return () => {
      cancelled = true
      window.clearTimeout(t)
      if (rafRef.current) window.clearTimeout(rafRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size.w, reduceMotion])

  // ── pointer: tap a frame, or drag a lasso ────────────────────────────────────
  const toClient = (e: React.PointerEvent): Pt => {
    const base = sheetRef.current!.getBoundingClientRect()
    return { x: e.clientX - base.left, y: e.clientY - base.top }
  }

  const onDown = (e: React.PointerEvent) => {
    if (size.w === 0) return
    setDemoD("") // any interaction cancels the ghost
    setHandedOver(true)
    drawingRef.current = true
    movedRef.current = 0
    const p = toClient(e)
    startPtRef.current = p
    draftRef.current = [p]
    setDraft([p])
    ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
  }

  const onMove = (e: React.PointerEvent) => {
    if (!drawingRef.current) return
    const p = toClient(e)
    const last = draftRef.current[draftRef.current.length - 1]
    if (last) movedRef.current += Math.hypot(p.x - last.x, p.y - last.y)
    draftRef.current.push(p)
    // throttle state writes a touch by only committing every other point
    setDraft(draftRef.current.slice())
  }

  const toggleFrame = useCallback(
    (idx: number, rect: { x: number; y: number; w: number; h: number }) => {
      setKept((prev) => {
        const next = new Set(prev)
        if (next.has(idx)) {
          next.delete(idx)
          setAutoLoops((al) => {
            const { [idx]: _drop, ...rest } = al
            return rest
          })
        } else {
          next.add(idx)
          setAutoLoops((al) => ({
            ...al,
            [idx]: pointsToPath(
              loopAroundRect(rect.x, rect.y, rect.w, rect.h, idx),
            ),
          }))
          native.logMixpanelEvent("html_proof_kept", { count: 1 })
        }
        return next
      })
    },
    [],
  )

  const onUp = () => {
    if (!drawingRef.current) return
    drawingRef.current = false
    const pts = draftRef.current.slice()
    setDraft([])
    draftRef.current = []
    const rects = frameRects()

    // a short press = a tap → ring / un-ring the frame under the finger
    if (movedRef.current < 12 && startPtRef.current) {
      const sp = startPtRef.current
      const hit = rects.find(
        (r) =>
          sp.x >= r.x && sp.x <= r.x + r.w && sp.y >= r.y && sp.y <= r.y + r.h,
      )
      if (hit) toggleFrame(hit.idx, hit)
      return
    }

    // a drag = a lasso → keep every frame whose centre falls inside the loop
    if (pts.length >= 3) {
      const enclosed = rects
        .filter((r) => inPolygon({ x: r.cx, y: r.cy }, pts))
        .map((r) => r.idx)
      if (enclosed.length) {
        setLassoLoops((l) => [
          ...l,
          { id: ++loopIdRef.current, d: pointsToPath(pts) },
        ])
        fileToVault(enclosed)
      }
    }
  }

  const circleWholeRoll = () => {
    setDemoD("")
    setHandedOver(true)
    const rects = frameRects()
    if (!rects.length) return
    const minX = Math.min(...rects.map((r) => r.x))
    const minY = Math.min(...rects.map((r) => r.y))
    const maxX = Math.max(...rects.map((r) => r.x + r.w))
    const maxY = Math.max(...rects.map((r) => r.y + r.h))
    // inset so the enthusiastic loop still hugs inside the sheet (padding is capped)
    const loop = loopAroundRect(
      minX + 16,
      minY + 12,
      maxX - minX - 32,
      maxY - minY - 24,
      0.4,
    )
    setLassoLoops((l) => [
      ...l,
      { id: ++loopIdRef.current, d: pointsToPath(loop) },
    ])
    fileToVault(rects.map((r) => r.idx))
    native.logMixpanelEvent("html_proof_whole_roll", {})
  }

  const clearMarks = () => {
    setKept(new Set())
    setLassoLoops([])
    setAutoLoops({})
    setDemoD("")
    native.logMixpanelEvent("html_proof_clear", {})
  }

  // ── plan + purchase wiring ────────────────────────────────────────────────────
  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  // ── copy ───────────────────────────────────────────────────────────────────
  const headline = complete
    ? "The whole roll — kept."
    : "Which of these is a keeper?"
  const sub = complete
    ? "Every frame filed to your vault: full-quality originals, private, and backed up on every device."
    : "Loop this month's keepers with the grease pencil. Premium keeps every original either way — full quality, private, backed up."

  const hint = complete
    ? "Nice roll. This is what Premium keeps for you automatically, every month."
    : keptCount > 0
      ? `${keptCount} filed to your vault — loop more, or keep the whole roll.`
      : "Drag a loop around the keepers · tap a frame · or circle the whole roll."

  const ctaLabel =
    plan === "yearly"
      ? hasTrial
        ? `Start ${trialDays} days free`
        : "Keep every roll — Yearly"
      : "Choose monthly"
  const ctaSub =
    plan === "yearly"
      ? hasTrial
        ? `${trialDays} days free, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · auto-renews · cancel anytime`
      : `${monthlyPrice}/mo · auto-renews · cancel anytime`

  const progressPct = Math.round((keptCount / TOTAL) * 100)

  return (
    <div className={styles.page}>
      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.brandKey} aria-hidden>
            <Lock size={13} />
          </span>
          <span>Private Photo Vault</span>
          <span className={styles.premiumPip}>Premium</span>
        </div>
        <h1 className={`${styles.title} ${complete ? styles.titleDone : ""}`}>
          {headline}
        </h1>
        <p className={styles.sub}>{sub}</p>
      </header>

      {/* ── the light table + proof sheet ─────────────────────────────────────── */}
      <div className={styles.lightbox}>
        <div className={styles.sheetHead}>
          <span className={styles.rollTag}>
            <span className={styles.rollDot} aria-hidden />
            ROLL 01 · THIS MONTH
          </span>
          <span className={styles.keptTag}>
            <span className={styles.keptTagLock} aria-hidden>
              <Lock size={10} />
            </span>
            KEPT {keptCount}/{TOTAL}
          </span>
        </div>

        <div
          ref={sheetRef}
          className={styles.sheet}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          role="group"
          aria-label="Proof sheet — loop or tap the photos you want to keep"
        >
          {Array.from({ length: Math.ceil(TOTAL / COLS) }, (_, row) => (
            <div key={row} className={styles.strip}>
              <span className={styles.sprockets} aria-hidden />
              <div className={styles.stripFrames}>
                {FRAMES.slice(row * COLS, row * COLS + COLS).map((f, c) => {
                  const i = row * COLS + c
                  const isKept = kept.has(i)
                  return (
                    <div
                      key={i}
                      data-frame={i}
                      className={`${styles.frame} ${isKept ? styles.frameKept : ""}`}
                    >
                      <span className={styles.frameNo}>{f.label}</span>
                      <img
                        src={f.src}
                        alt=""
                        draggable={false}
                        onError={(e) =>
                          ((e.currentTarget as HTMLImageElement).style.opacity =
                            "0")
                        }
                      />
                      <span className={styles.keptBadge} aria-hidden>
                        <Tick size={10} />
                      </span>
                    </div>
                  )
                })}
              </div>
              <span className={styles.sprockets} aria-hidden />
            </div>
          ))}

          {/* grease-pencil overlay: committed loops + the live draft + demo ghost */}
          {size.w > 0 && (
            <svg
              className={styles.ink}
              viewBox={`0 0 ${size.w} ${size.h}`}
              width={size.w}
              height={size.h}
              aria-hidden
            >
              {Object.entries(autoLoops).map(([k, d]) => (
                <path key={`a${k}`} className={styles.loop} d={d} />
              ))}
              {lassoLoops.map((l) => (
                <path key={l.id} className={styles.loop} d={l.d} />
              ))}
              {draft.length > 1 && (
                <path
                  className={styles.loopLive}
                  d={pointsToPath(draft, false)}
                />
              )}
              {demoD && (
                <path className={styles.loopDemo} pathLength={1} d={demoD} />
              )}
            </svg>
          )}

          {/* the resting grease pencil, lower-right */}
          <span
            className={`${styles.pencilRest} ${handedOver ? styles.pencilReady : ""}`}
            aria-hidden
          >
            <Pencil size={16} />
          </span>
        </div>

        {/* endowment / goal-gradient meter under the sheet */}
        <div className={styles.meter} aria-hidden>
          <span
            className={styles.meterFill}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <p
        className={`${styles.hintLine} ${complete ? styles.hintDone : ""}`}
        aria-live="polite"
      >
        {hint}
      </p>

      <div className={styles.tools}>
        <button
          className={styles.toolPrimary}
          onClick={circleWholeRoll}
          disabled={complete}
        >
          <Pencil size={14} />
          {complete ? "Whole roll circled" : "Circle the whole roll"}
        </button>
        <button
          className={styles.toolGhost}
          onClick={clearMarks}
          disabled={keptCount === 0}
        >
          Clear
        </button>
      </div>

      {/* what "keeping" actually means — the honest Premium bundle, one short list */}
      <ul className={styles.keeps}>
        {[
          "Every original in full quality — never recompressed",
          "Private & encrypted — only your passcode opens it",
          "Auto-organised & backed up on every device",
        ].map((t) => (
          <li key={t}>
            <span className={styles.keepDot} aria-hidden>
              <Tick size={10} />
            </span>
            {t}
          </li>
        ))}
      </ul>

      {/* ── plans — yearly featured, monthly small & secondary ─────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.planSel : ""}`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.planMain}>
            <span className={styles.planName}>Yearly</span>
            <span className={styles.planNote}>
              Keep every roll, automatically
            </span>
          </span>
          <span className={styles.planPrice}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/yr</span>
            <span className={styles.planSmall}>
              {yearlyPerMonth}/mo
              {savingsPct > 0 ? ` · save ${savingsPct}%` : ""}
            </span>
          </span>
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
          )}
        </button>

        <button
          className={`${styles.planMonth} ${plan === "monthly" ? styles.planMonthSel : ""}`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>{ctaLabel}</span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        The loop only chooses favourites to relive — it never deletes. Your
        photos are always yours.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default ProofPaywall
